import type { Task } from '@/types/task'
import { JWTHttpClient } from '@/services/JWTHttpClient'

export interface CreateTaskResponse {
  success: boolean
  createdTask?: Task
  task?: Task
}

// Get the tasks service URL from environment variable, fallback to stage API Gateway
const TASKS_SERVICE_URL = import.meta.env.VITE_TASKS_SERVICE_URL || '/service-tasks-stage'

// Create JWT HTTP client for Task Service API calls
const jwtHttpClient = new JWTHttpClient(TASKS_SERVICE_URL)

export async function fetchCaseTasks(caseId: string): Promise<Task[]> {
  try {
    const response = await jwtHttpClient.get<Task[]>('/v1/tasks', { 
      caseId: caseId 
    }, {
      'x-ovationcxm-source': 'com.ovationcxm.tasks/ui:tasks'
    })
    return response
  } catch (error) {
    console.error('Failed to fetch case tasks:', error)
    throw new Error('Failed to fetch case tasks')
  }
}

export async function fetchUserTasks(userId: string): Promise<Task[]> {
  try {
    const response = await jwtHttpClient.get<Task[]>('/v1/tasks', { 
      userId: userId 
    }, {
      'x-ovationcxm-source': 'com.ovationcxm.tasks/ui:mywork'
    })
    return response
  } catch (error) {
    // Check if it's a 404 error (user not found or no tasks)
    if (error instanceof Error && error.message.includes('404')) {
      console.warn(`No tasks found for user ${userId} (404 - user may not exist or has no tasks)`)
      return [] // Return empty array instead of throwing
    }
    console.error('Failed to fetch user tasks:', error)
    throw new Error('Failed to fetch user tasks')
  }
}

export async function fetchTeamTasks(teamId: string): Promise<Task[]> {
  try {
    const response = await jwtHttpClient.get<Task[]>('/v1/tasks', { 
      teamId: teamId 
    }, {
      'x-ovationcxm-source': 'com.ovationcxm.tasks/ui:mywork'
    })
    return response
  } catch (error) {
    // Check if it's a 404 error (team not found or no tasks)
    if (error instanceof Error && error.message.includes('404')) {
      console.warn(`No tasks found for team ${teamId} (404 - team may not exist or has no tasks)`)
      return [] // Return empty array instead of throwing
    }
    console.error('Failed to fetch team tasks:', error)
    throw new Error('Failed to fetch team tasks')
  }
}

export async function fetchTaskById(taskId: string): Promise<Task> {
  try {
    const response = await jwtHttpClient.get<Task>(`/v1/tasks/${encodeURIComponent(taskId)}`, undefined, {
      'x-ovationcxm-source': 'com.ovationcxm.tasks/ui:mywork'
    })
    return response
  } catch (error) {
    console.error('Failed to fetch task:', error)
    throw new Error('Failed to fetch task')
  }
}

export async function updateTaskStatus(task: Task, status: string): Promise<Task> {
  try {
    // Only send the fields required by the backend
    const { id, name, description, dueDate, deleted, links } = task
    const updatedTask: any = {
      id,
      name,
      description,
      dueDate: dueDate && dueDate.trim() !== '' ? dueDate : null,
      deleted,
      links,
      status,
    }
    
    // Ensure we never send owner_team_id or owner_user_id at the root level
    // These should only exist within the links array
    delete updatedTask.owner_team_id
    delete updatedTask.owner_user_id
    
    const response = await jwtHttpClient.put<any>(`/v1/tasks/${encodeURIComponent(task.id)}`, updatedTask, undefined, {
      'x-ovationcxm-source': 'com.ovationcxm.tasks/ui:mywork'
    })
    
    return response.createdTask || response.task || response
  } catch (error) {
    console.error('Failed to update task status:', error)
    throw new Error('Failed to update task status')
  }
}

export async function updateTask(taskToUpdate: Task, updates: Partial<Task>): Promise<Task> {
  try {
    const { id, name, description, dueDate, deleted, links, status } = taskToUpdate
    
    // Prepare the updated task data, merging updates with existing data
    const updatedTask: any = {
      id,
      name: updates.name !== undefined ? updates.name : name,
      description: updates.description !== undefined ? updates.description : description,
      dueDate: updates.dueDate !== undefined 
        ? (updates.dueDate && updates.dueDate.trim() !== '' ? updates.dueDate : null)
        : (dueDate && dueDate.trim() !== '' ? dueDate : null),
      deleted: updates.deleted !== undefined ? updates.deleted : deleted,
      links: updates.links !== undefined ? updates.links : links,
      status: updates.status !== undefined ? updates.status : status,
    }
    
    // Ensure we never send owner_team_id or owner_user_id at the root level
    // These should only exist within the links array
    delete updatedTask.owner_team_id
    delete updatedTask.owner_user_id
    
    const response = await jwtHttpClient.put<any>(`/v1/tasks/${encodeURIComponent(taskToUpdate.id)}`, updatedTask, undefined, {
      'x-ovationcxm-source': 'com.ovationcxm.tasks/ui:tasks'
    })
    
    return response.createdTask || response.task || response
  } catch (error) {
    console.error('Failed to update task:', error)
    throw new Error('Failed to update task')
  }
}

export async function createTask(taskData: Partial<Task>): Promise<Task> {
  try {
    // Clean the task data to ensure owner_team_id and owner_user_id are not at root level
    const cleanedTaskData: any = { ...taskData }
    
    // Ensure we never send owner_team_id or owner_user_id at the root level
    // These should only exist within the links array
    delete cleanedTaskData.owner_team_id
    delete cleanedTaskData.owner_user_id
    
    const response = await jwtHttpClient.post<CreateTaskResponse>('/v1/tasks', cleanedTaskData, undefined, {
      'x-ovationcxm-source': 'com.ovationcxm.tasks/ui:tasks'
    })
    
    if (!response.success) {
      throw new Error('Failed to create task')
    }
    
    return response.createdTask || response.task!
  } catch (error) {
    console.error('Failed to create task:', error)
    throw new Error('Failed to create task')
  }
}

export async function fetchAllTasks(orgId: string): Promise<Task[]> {
  try {
    const response = await jwtHttpClient.get<Task[]>('/v1/tasks', { 
      orgId: orgId 
    }, {
      'x-ovationcxm-source': 'com.ovationcxm.tasks/ui:mywork'
    })
    return response
  } catch (error) {
    // Check if it's a 404 error (org not found or no tasks)
    if (error instanceof Error && error.message.includes('404')) {
      console.warn(`No tasks found for org ${orgId} (404 - org may not exist or has no tasks)`)
      return [] // Return empty array instead of throwing
    }
    console.error('Failed to fetch all tasks:', error)
    throw new Error('Failed to fetch all tasks')
  }
}

export interface DeleteTaskResponse {
  status: number
  success: boolean
}

export async function deleteTask(taskId: string): Promise<DeleteTaskResponse> {
  try {
    const response = await jwtHttpClient.delete<DeleteTaskResponse>(`/v1/tasks/${encodeURIComponent(taskId)}`, undefined, {
      'x-ovationcxm-source': 'com.ovationcxm.tasks/ui:tasks'
    })
    return response
  } catch (error) {
    console.error('Failed to delete task:', error)
    throw new Error('Failed to delete task')
  }
} 